import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DrawingLayer from '@component/gridgio/workspace/workspace-canvas/drawing-layer';
import type { IArea, IDrawing } from '@datatype/gridgio/type';
import * as color from '@constant/color';
import * as size from '@constant/size';
import PageNavigator from '@design-system/page-navigator';
import Gap from '@component/common/gap';
import CanvasToolbar, { type TCanvasToolbarAction } from '@design-system/canvas-toolbar';
import { ETheme } from '@datatype/type';
import type { IWorkspaceView } from '@datatype/gridgio/type';
import { useWorkspaceView, useWorkspaceGesture } from '@hook/gridgio/useWorkspace';

interface IProps {
  drawing: Pick<IDrawing, 'url' | 'type'>;
  page?: number;
  rotation: number;
  area: IArea;
  width?: string;
  height?: string;
  shadow?: boolean;
  pagination?: boolean;
  controls?: boolean;
}

const Wrapper = styled.div<{ $width: string; $height: string; $shadow: boolean }>`
  position: relative;
  display: flex;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  flex-direction: column;
  padding: ${size.MARGIN_1};
  box-sizing: border-box;
  background-color: ${color.GRAY_3};
  background-image:
    linear-gradient(${color.GRAY_2}40 1px, transparent 1px),
    linear-gradient(90deg, ${color.GRAY_2}40 1px, transparent 1px);
  background-size: 24px 24px;
  // border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  box-shadow: ${({ $shadow }) => ($shadow ? color.SURFACE_SHADOW : 'none')};
`;

const DrawingContainer = styled.div<{ $moveActive: boolean }>`
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: transparent;
  cursor: ${({ $moveActive }) => ($moveActive ? 'grab' : 'default')};
  touch-action: ${({ $moveActive }) => ($moveActive ? 'none' : 'auto')};
`;

const ControlContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${size.MARGIN_1};
  flex-shrink: 0;
`;

const PageNavigatorContainer = styled.div`
  margin-left: auto;
`;

const LayerContainer = styled.div<{ $width: number; $height: number; $view: IWorkspaceView }>`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  flex-shrink: 0;
  transform: ${({ $view }) => `translate(${$view.x}px, ${$view.y}px) scale(${$view.zoom})`};
  transform-origin: center;
`;

const PlanViewer: React.FC<IProps> = ({
  drawing,
  page,
  rotation,
  area,
  width = '350px',
  height = '160px',
  shadow = true,
  pagination = false,
  controls = false,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(page ?? 1);
  const [previewRotation, setPreviewRotation] = useState(rotation);
  const [totalPage, setTotalPage] = useState(1);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const handleSizeChange = useCallback((width: number, height: number) => setCanvasSize({ width, height }), []);
  const { view, setView, moveActive, resetView, disableMove, handleToolbarClick } = useWorkspaceView();
  const { handlePointerDown, handlePointerMove, handlePointerEnd } = useWorkspaceGesture({
    viewportRef,
    moveActive: controls && moveActive,
    handleViewChange: setView,
  });
  const handlePageChange = useCallback((count: number) => setTotalPage(count), []);

  const handleControlClick = (action: TCanvasToolbarAction) => {
    if (action === 'rotate') {
      setPreviewRotation((current) => (current + 90) % 360);
      return;
    }
    handleToolbarClick(action);
  };

  useEffect(() => setPreviewRotation(rotation), [drawing.url, drawing.type, rotation]);

  useEffect(() => setCurrentPage(page ?? 1), [page, drawing.url, drawing.type]);

  useEffect(() => {
    resetView();
    disableMove();
  }, [drawing.url, drawing.type, previewRotation, controls, resetView, disableMove]);

  return (
    <Wrapper $width={width} $height={height} $shadow={shadow}>
      {(controls || (pagination && drawing.type === 'pdf' && totalPage > 1)) && (
        <>
          <ControlContainer>
            {controls && (
              <CanvasToolbar
                items={['zoom', 'fit', 'rotate','move']}
                zoomRatio={Math.round(view.zoom * 100)}
                moveActive={moveActive}
                theme={ETheme.GRAY}
                size='medium'
                height={size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT}
                border
                handleClick={handleControlClick}
              />
            )}
            {pagination && drawing.type === 'pdf' && totalPage > 1 && (
              <PageNavigatorContainer>
                <PageNavigator currentPage={currentPage} totalPage={totalPage} handleChange={setCurrentPage} />
              </PageNavigatorContainer>
            )}
          </ControlContainer>
          <Gap height={size.MARGIN_1} />
        </>
      )}
      <DrawingContainer
        ref={viewportRef}
        $moveActive={controls && moveActive}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onLostPointerCapture={handlePointerEnd}>
        <LayerContainer $width={canvasSize.width} $height={canvasSize.height} $view={view}>
          <DrawingLayer
            drawing={drawing}
            page={currentPage}
            viewportRef={viewportRef}
            rotation={previewRotation}
            cropArea={area}
            handleSizeChange={handleSizeChange}
            handlePageChange={handlePageChange}
          />
        </LayerContainer>
      </DrawingContainer>
    </Wrapper>
  );
};

export default PlanViewer;
